import { auth } from '../config/firebase';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.socialness.ai';

/**
 * Converts an image URL to a File object
 * Attempts to use the server as a proxy to avoid CORS issues with external URLs
 * If the proxy endpoint doesn't exist, falls back to direct upload of the URL string
 * 
 * @param url The image URL to convert
 * @returns Promise with a File object created from the URL, or null if CORS prevents download
 */
export const imageUrlToFile = async (url: string): Promise<File | null> => {
  try {
    console.log(`Converting image URL to File: ${url}`);
    
    // For external URLs, we need to use our server as a proxy to avoid CORS issues
    // Get Firebase auth token
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    // Try to use our proxy endpoint to fetch the image
    console.log('Attempting to use server proxy to download image and avoid CORS issues');
    const proxyUrl = `${API_URL}/storage/proxy-download`;
    
    try {
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url })
      });
      
      if (!response.ok) {
        console.warn(`Proxy endpoint returned error: ${response.status} ${response.statusText}`);
        throw new Error(`Proxy failed: ${response.status}`);
      }
      
      // Get the image as a blob
      const blob = await response.blob();
      
      // Determine file type from blob or URL
      let fileType = blob.type || 'image/jpeg';
      if (!fileType || fileType === 'application/octet-stream') {
        // Try to determine from URL extension
        const extension = url.split('.').pop()?.toLowerCase();
        if (extension === 'png') fileType = 'image/png';
        else if (extension === 'jpg' || extension === 'jpeg') fileType = 'image/jpeg';
        else if (extension === 'gif') fileType = 'image/gif';
        else if (extension === 'webp') fileType = 'image/webp';
        else fileType = 'image/jpeg'; // Default fallback
      }
      
      // Create a filename based on the URL and timestamp
      const fileExtension = fileType.split('/')[1] || 'jpg';
      const fileName = `image-${Date.now()}.${fileExtension}`;
      
      // Create a new File object from the blob
      const file = new File([blob], fileName, { type: fileType });
      
      console.log(`Successfully converted URL to File: ${fileName} (${fileType}, ${Math.round(blob.size / 1024)}KB)`);
      return file;
    } catch (proxyError) {
      // If proxy endpoint doesn't exist or fails, we'll need to use a different approach
      console.warn('Proxy download failed:', proxyError);
      console.warn('Falling back to direct URL submission');
      
      // We'll return null to indicate we couldn't download
      // The calling code should handle this by using the URL directly
      return null;
    }
  } catch (error) {
    console.error('Error converting image URL to File:', error);
    // Return null to indicate failure but allow form submission to continue
    return null;
  }
};

/**
 * Uploads a profile image file using the server's upload endpoint
 * This approach avoids CORS issues compared to direct S3 uploads
 * 
 * @param file The image file to upload
 * @param brandId Optional brand ID to associate with the uploaded image
 * @returns Promise with the public URL of the uploaded image
 */
export const uploadProfileImage = async (
  file: File,
  brandId: string = ''
): Promise<string> => {
  if (!file) throw new Error('No file provided');
  
  console.log(`Processing profile image upload: ${file.name} (${file.type}, ${Math.round(file.size / 1024)}KB)`);
  console.log(`Using API URL: ${API_URL}`);
  
  try {
    // Step 1: Get Firebase auth token
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    // Step 2: Use the upload endpoint which handles CORS properly
    console.log('Uploading profile image via upload endpoint...');
    
    // Create a FormData object to send the file
    const formData = new FormData();
    formData.append('file', file);
    
    // Add brandId if provided
    if (brandId) {
      formData.append('brandId', brandId);
      console.log(`Including brandId: ${brandId} in upload request`);
    }
    
    // Send the upload request
    const uploadUrl = `${API_URL}/storage/upload`;
    console.log(`Making POST request to: ${uploadUrl}`);
    
    const uploadResponse = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // Don't set Content-Type header - browser will set it correctly for FormData
      },
      body: formData
    });
    
    console.log('Upload response status:', uploadResponse.status);
    console.log('Upload response statusText:', uploadResponse.statusText);
    
    if (!uploadResponse.ok) {
      let errorText;
      try {
        const errorData = await uploadResponse.json();
        errorText = JSON.stringify(errorData);
      } catch (e) {
        errorText = await uploadResponse.text();
      }
      throw new Error(`Failed to upload image: ${uploadResponse.status} ${uploadResponse.statusText}. Details: ${errorText}`);
    }
    
    // Get the response data
    const responseText = await uploadResponse.text();
    console.log('Raw response text:', responseText);
    
    // Try to parse as JSON if possible
    let imageUrl = '';
    try {
      // Check if response is JSON
      if (responseText.trim().startsWith('{') || responseText.trim().startsWith('[')) {
        const responseData = JSON.parse(responseText);
        console.log('Parsed response data:', responseData);
        
        // Try to find URL in standard locations
        if (typeof responseData === 'object' && responseData !== null) {
          imageUrl = responseData.url || 
                    (responseData.data && responseData.data.url) || 
                    responseData.fileUrl || 
                    responseData.mediaUrl ||
                    responseData.imageUrl ||
                    '';
        }
      } else if (responseText.trim().startsWith('http')) {
        // Response is directly a URL
        imageUrl = responseText.trim();
      }
    } catch (error) {
      console.warn('Error parsing response as JSON, will try direct URL:', error);
      // If parsing fails, check if the response itself looks like a URL
      if (responseText.trim().startsWith('http')) {
        imageUrl = responseText.trim();
      }
    }
    
    // If we still don't have a URL, try one more fallback
    if (!imageUrl && responseText.includes('http')) {
      // Try to extract URL from text if it contains http
      const urlMatch = responseText.match(/(https?:\/\/[^\s"']+)/);
      if (urlMatch && urlMatch[0]) {
        imageUrl = urlMatch[0];
        console.log('Extracted URL from response text:', imageUrl);
      }
    }
    
    if (!imageUrl) {
      throw new Error(`Could not find image URL in response. Raw response: ${responseText.substring(0, 100)}...`);
    }
    
    console.log('Profile image successfully uploaded');
    console.log('Using image URL:', imageUrl);
    
    return imageUrl;
  } catch (error: any) {
    console.error('Error uploading profile image:', error);
    throw new Error(`Image upload failed: ${error.message || 'Unknown error'}`);
  }
}; 